<script lang="ts">
	import { page } from '$app/state';
	import {
		absoluteUrl,
		marketingHreflangLinks,
		marketingStructuredData,
		ogLocale,
		ogLocaleAlternates,
		MARKETING_DEFAULT_OG_IMAGE,
		type MarketingSeoMeta
	} from '$lib/utils/marketing-seo';
	import { getLocale } from '@/paraglide/runtime';

	interface Props {
		meta: MarketingSeoMeta;
		/** De-localized path for hreflang (e.g. `/regala-zarpar`). */
		canonicalPath: string;
		/** Override canonical URL (checkout → gift landing). */
		canonicalUrl?: string;
		robots?: string;
		includeJsonLd?: boolean;
		/** When set, replaces default WebPage JSON-LD (e.g. FAQPage graph). */
		structuredData?: Record<string, unknown> | null;
	}

	let {
		meta,
		canonicalPath,
		canonicalUrl: canonicalUrlOverride,
		robots = 'index, follow',
		includeJsonLd = true,
		structuredData = null
	}: Props = $props();

	const locale = $derived(getLocale());
	const pageUrl = $derived(canonicalUrlOverride ?? absoluteUrl(page.url.pathname));
	const ogImage = $derived(absoluteUrl(meta.image ?? MARKETING_DEFAULT_OG_IMAGE));
	const hreflangLinks = $derived(marketingHreflangLinks(canonicalPath));
	const ogAlternateLocales = $derived(ogLocaleAlternates(locale));
	const jsonLd = $derived(
		includeJsonLd
			? JSON.stringify(
					structuredData ??
						marketingStructuredData({
							title: meta.title,
							description: meta.description,
							pageUrl
						})
				)
			: null
	);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<meta name="robots" content={robots} />

	<link rel="canonical" href={pageUrl} />
	{#each hreflangLinks as link (link.lang)}
		<link rel="alternate" hreflang={link.lang} href={link.href} />
	{/each}

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:type" content="website" />
	<meta property="og:url" content={pageUrl} />
	<meta property="og:site_name" content="Zarpar" />
	<meta property="og:locale" content={ogLocale(locale)} />
	{#each ogAlternateLocales as alternateLocale (alternateLocale)}
		<meta property="og:locale:alternate" content={alternateLocale} />
	{/each}
	<meta property="og:image" content={ogImage} />
	<meta property="og:image:alt" content={meta.title} />

	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={meta.title} />
	<meta name="twitter:description" content={meta.description} />
	<meta name="twitter:image" content={ogImage} />

	{#if meta.preloadImage}
		<link rel="preload" as="image" href={absoluteUrl(meta.preloadImage)} />
	{/if}

	{#if jsonLd}
		{@html `<script type="application/ld+json">${jsonLd}</script>`}
	{/if}
</svelte:head>
